import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

// Types
export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: Record<string, unknown>;
  correlationId?: string;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Record<string, string>;
}

// Storage keys
const SESSION_TOKEN_KEY = 'mip_session_token';
const WORKSPACE_ID_KEY = 'mip_workspace_id';
const CLIENT_ID_KEY = 'mip_client_id';
const ORG_ID_KEY = 'mip_organization_id';

// Event for session expiry
export const SESSION_EXPIRED_EVENT = 'mip:session:expired';

// Create axios instance
const createApiClient = (): AxiosInstance => {
  const baseURL = import.meta.env.VITE_API_URL || '/api';

  const instance = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Add session token
      const token = localStorage.getItem(SESSION_TOKEN_KEY);
      if (token) {
        config.headers['X-Session-Token'] = token;
      }

      // Add tenant context
      const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);
      if (workspaceId) {
        config.headers['X-Workspace-ID'] = workspaceId;
      }

      const clientId = localStorage.getItem(CLIENT_ID_KEY);
      if (clientId) {
        config.headers['X-Client-ID'] = clientId;
      }

      const orgId = localStorage.getItem(ORG_ID_KEY);
      if (orgId) {
        config.headers['X-Organization-ID'] = orgId;
      }

      // Add correlation ID for request tracing
      config.headers['X-Correlation-ID'] = generateCorrelationId();

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      const apiError = parseApiError(error);

      // Handle 401 — but ONLY as a genuine session EXPIRY, i.e. a request
      // that actually carried a session token got rejected. A 401 on an
      // anonymous request (the pre-login /auth/me probe, or a bad-credential
      // /auth/login) must NOT wipe state or redirect — doing so created a
      // login redirect loop that raced with and erased the freshly-set token
      // (release-blocker, 2026-08-12).
      const hadToken = Boolean(error.config?.headers?.['X-Session-Token']);
      if (apiError.status === 401 && hadToken) {
        localStorage.removeItem(SESSION_TOKEN_KEY);
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }

      return Promise.reject(apiError);
    }
  );

  return instance;
};

// Parse error from axios error
const parseApiError = (error: AxiosError): ApiError => {
  if (error.response) {
    const { status, data, headers } = error.response;
    const responseData = data as Record<string, unknown>;

    return {
      status,
      code: String(responseData?.code || getErrorCodeFromStatus(status)),
      message: String(responseData?.message || getErrorMessageFromStatus(status)),
      details: responseData?.details as Record<string, unknown>,
      correlationId: headers['x-correlation-id'] as string,
    };
  }

  if (error.request) {
    return {
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'Unable to connect to the server',
    };
  }

  return {
    status: 0,
    code: 'REQUEST_ERROR',
    message: error.message,
  };
};

// Get error code from HTTP status
const getErrorCodeFromStatus = (status: number): string => {
  const codes: Record<number, string> = {
    400: 'BAD_REQUEST',
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    409: 'CONFLICT',
    422: 'VALIDATION_ERROR',
    429: 'RATE_LIMITED',
    500: 'SERVER_ERROR',
    502: 'BAD_GATEWAY',
    503: 'SERVICE_UNAVAILABLE',
  };
  return codes[status] || 'UNKNOWN_ERROR';
};

// Get error message from HTTP status
const getErrorMessageFromStatus = (status: number): string => {
  const messages: Record<number, string> = {
    400: 'Invalid request',
    401: 'Authentication required',
    403: 'Access denied',
    404: 'Resource not found',
    409: 'Resource conflict',
    422: 'Validation failed',
    429: 'Too many requests',
    500: 'Internal server error',
    502: 'Bad gateway',
    503: 'Service unavailable',
  };
  return messages[status] || 'An error occurred';
};

// Generate correlation ID
const generateCorrelationId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// Create singleton instance
const apiClient = createApiClient();

// API helper functions
export const api = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.get<T>(url, config);
    return {
      data: response.data,
      status: response.status,
      headers: response.headers as Record<string, string>,
    };
  },

  post: async <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.post<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
      headers: response.headers as Record<string, string>,
    };
  },

  put: async <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.put<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
      headers: response.headers as Record<string, string>,
    };
  },

  patch: async <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.patch<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
      headers: response.headers as Record<string, string>,
    };
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.delete<T>(url, config);
    return {
      data: response.data,
      status: response.status,
      headers: response.headers as Record<string, string>,
    };
  },
};

// Session management
export const session = {
  getToken: (): string | null => localStorage.getItem(SESSION_TOKEN_KEY),
  setToken: (token: string): void => localStorage.setItem(SESSION_TOKEN_KEY, token),
  clearToken: (): void => localStorage.removeItem(SESSION_TOKEN_KEY),
  isAuthenticated: (): boolean => !!localStorage.getItem(SESSION_TOKEN_KEY),
};

// Tenant context management
export const tenantContext = {
  getOrganizationId: (): string | null => localStorage.getItem(ORG_ID_KEY),
  setOrganizationId: (id: string): void => localStorage.setItem(ORG_ID_KEY, id),
  clearOrganizationId: (): void => localStorage.removeItem(ORG_ID_KEY),

  getClientId: (): string | null => localStorage.getItem(CLIENT_ID_KEY),
  setClientId: (id: string): void => localStorage.setItem(CLIENT_ID_KEY, id),
  clearClientId: (): void => localStorage.removeItem(CLIENT_ID_KEY),

  getWorkspaceId: (): string | null => localStorage.getItem(WORKSPACE_ID_KEY),
  setWorkspaceId: (id: string): void => localStorage.setItem(WORKSPACE_ID_KEY, id),
  clearWorkspaceId: (): void => localStorage.removeItem(WORKSPACE_ID_KEY),

  clearAll: (): void => {
    localStorage.removeItem(ORG_ID_KEY);
    localStorage.removeItem(CLIENT_ID_KEY);
    localStorage.removeItem(WORKSPACE_ID_KEY);
  },
};

export default apiClient;
